import { useState, useEffect, useMemo } from 'react';
import { TextInput, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@c2fo/liquidity';
import { QuestionCircleIcon } from '@c2fo/liquidity/icons';
import cronstrue from 'cronstrue';

interface CronEditorProps {
  value: string;
  onChange: (expression: string) => void;
}

type CronPreset = 'custom' | 'every-minute' | 'every-hour' | 'daily' | 'weekly' | 'monthly';

const PRESET_OPTIONS: { value: CronPreset; label: string; expression?: string }[] = [
  { value: 'every-minute', label: 'Every minute', expression: '* * * * *' },
  { value: 'every-hour', label: 'Every hour', expression: '0 * * * *' },
  { value: 'daily', label: 'Daily at specific time' },
  { value: 'weekly', label: 'Weekly on specific day' },
  { value: 'monthly', label: 'Monthly on specific day' },
  { value: 'custom', label: 'Custom schedule' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`,
}));

const MINUTES = Array.from({ length: 60 }, (_, i) => ({
  value: String(i),
  label: i.toString().padStart(2, '0'),
}));

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const parseCronExpression = (
  expression: string,
): { preset: CronPreset; minute: string; hour: string; dayOfMonth: string; dayOfWeek: string } => {
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 5) {
    return { preset: 'custom', minute: '0', hour: '0', dayOfMonth: '1', dayOfWeek: '0' };
  }

  const [minute, hour, dayOfMonth, , dayOfWeek] = parts;

  if (expression === '* * * * *')
    return { preset: 'every-minute', minute: '0', hour: '0', dayOfMonth: '1', dayOfWeek: '0' };
  if (expression === '0 * * * *')
    return { preset: 'every-hour', minute: '0', hour: '0', dayOfMonth: '1', dayOfWeek: '0' };

  if (dayOfMonth === '*' && dayOfWeek === '*' && minute !== '*' && hour !== '*') {
    return { preset: 'daily', minute, hour, dayOfMonth: '1', dayOfWeek: '0' };
  }

  if (dayOfMonth === '*' && dayOfWeek !== '*' && minute !== '*' && hour !== '*') {
    return { preset: 'weekly', minute, hour, dayOfMonth: '1', dayOfWeek };
  }

  if (dayOfMonth !== '*' && dayOfWeek === '*' && minute !== '*' && hour !== '*') {
    return { preset: 'monthly', minute, hour, dayOfMonth, dayOfWeek: '0' };
  }

  return { preset: 'custom', minute: '0', hour: '0', dayOfMonth: '1', dayOfWeek: '0' };
};

export const CronEditor: React.FC<CronEditorProps> = ({ value, onChange }) => {
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [preset, setPreset] = useState<CronPreset>('daily');
  const [minute, setMinute] = useState('0');
  const [hour, setHour] = useState('0');
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [dayOfMonth, setDayOfMonth] = useState('1');

  useEffect(() => {
    if (value) {
      const parsed = parseCronExpression(value);
      setPreset(parsed.preset);
      setMinute(parsed.minute);
      setHour(parsed.hour);
      setDayOfWeek(parsed.dayOfWeek);
      setDayOfMonth(parsed.dayOfMonth);
      if (parsed.preset === 'custom') {
        setIsAdvanced(true);
      }
    }
  }, [value]);

  const buildExpression = (p: CronPreset, m: string, h: string, dom: string, dow: string): string => {
    switch (p) {
      case 'every-minute':
        return '* * * * *';
      case 'every-hour':
        return '0 * * * *';
      case 'daily':
        return `${m} ${h} * * *`;
      case 'weekly':
        return `${m} ${h} * * ${dow}`;
      case 'monthly':
        return `${m} ${h} ${dom} * *`;
      default:
        return value;
    }
  };

  const handlePresetChange = (newPreset: CronPreset) => {
    setPreset(newPreset);
    if (newPreset !== 'custom') {
      const expression = buildExpression(newPreset, minute, hour, dayOfMonth, dayOfWeek);
      onChange(expression);
    }
  };

  const handleFieldChange = (field: 'minute' | 'hour' | 'dayOfWeek' | 'dayOfMonth', val: string) => {
    const newMinute = field === 'minute' ? val : minute;
    const newHour = field === 'hour' ? val : hour;
    const newDayOfWeek = field === 'dayOfWeek' ? val : dayOfWeek;
    const newDayOfMonth = field === 'dayOfMonth' ? val : dayOfMonth;

    if (field === 'minute') setMinute(val);
    if (field === 'hour') setHour(val);
    if (field === 'dayOfWeek') setDayOfWeek(val);
    if (field === 'dayOfMonth') setDayOfMonth(val);

    const expression = buildExpression(preset, newMinute, newHour, newDayOfMonth, newDayOfWeek);
    onChange(expression);
  };

  const [showHelp, setShowHelp] = useState(false);

  const humanReadableCron = useMemo(() => {
    if (!value || !value.trim()) return '';
    try {
      return cronstrue.toString(value, { throwExceptionOnParseError: false });
    } catch {
      return 'Invalid expression';
    }
  }, [value]);

  if (isAdvanced) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Label className="block text-sm font-medium text-gray-700">Cron Expression</Label>
            <button
              type="button"
              onClick={() => setShowHelp(!showHelp)}
              className="text-gray-400 hover:text-gray-600"
              title="Show help"
            >
              <QuestionCircleIcon className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setIsAdvanced(false)}
            className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
          >
            Use visual editor
          </button>
        </div>

        {showHelp && (
          <div className="bg-gray-800 text-white rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-medium text-sm">Cron Expression Format</p>
              <button
                type="button"
                onClick={() => setShowHelp(false)}
                className="text-gray-400 hover:text-white text-xs"
              >
                Close
              </button>
            </div>
            <pre className="text-xs font-mono bg-gray-900 p-3 rounded overflow-x-auto">
              {`┌───────────── minute (0-59)
│ ┌─────────── hour (0-23)
│ │ ┌───────── day of month (1-31)
│ │ │ ┌─────── month (1-12)
│ │ │ │ ┌───── day of week (0-6, Sun=0)
│ │ │ │ │
* * * * *`}
            </pre>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="space-y-1">
                <p className="font-medium text-gray-300">Special Characters:</p>
                <p>
                  <code className="bg-gray-900 px-1 rounded">*</code> any value
                </p>
                <p>
                  <code className="bg-gray-900 px-1 rounded">,</code> list (e.g., 1,3,5)
                </p>
                <p>
                  <code className="bg-gray-900 px-1 rounded">-</code> range (e.g., 1-5)
                </p>
                <p>
                  <code className="bg-gray-900 px-1 rounded">/</code> step (e.g., */15 * * * * for every 15 minutes)
                </p>
              </div>
              <div className="space-y-1">
                <p className="font-medium text-gray-300">Examples:</p>
                <p>
                  <code className="bg-gray-900 px-1 rounded">0 9 * * *</code> Daily 9 AM
                </p>
                <p>
                  <code className="bg-gray-900 px-1 rounded">*/15 * * * *</code> Every 15 min
                </p>
                <p>
                  <code className="bg-gray-900 px-1 rounded">0 9 * * 1-5</code> Weekdays 9 AM
                </p>
                <p>
                  <code className="bg-gray-900 px-1 rounded">0 0 1 * *</code> 1st of month
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <TextInput
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            placeholder="* * * * *"
            className="w-40 font-mono"
            size="sm"
          />
          <span className="text-sm text-gray-600 italic">{humanReadableCron}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="block text-sm font-medium text-gray-700">Schedule</Label>
        <button
          type="button"
          onClick={() => {
            setIsAdvanced(true);
            setPreset('custom');
          }}
          className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
        >
          Advanced
        </button>
      </div>

      <div>
        <Label className="block text-xs text-gray-500 mb-1">Frequency</Label>
        <Select value={preset} onValueChange={(val) => handlePresetChange(val as CronPreset)}>
          <SelectTrigger className="w-full py-2.5" size="sm">
            <SelectValue placeholder="Select frequency" />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.filter((o) => o.value !== 'custom').map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(preset === 'daily' || preset === 'weekly' || preset === 'monthly') && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="block text-xs text-gray-500 mb-1">Hour</Label>
            <Select value={hour} onValueChange={(val) => handleFieldChange('hour', val)}>
              <SelectTrigger className="w-full py-2.5" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOURS.map((h) => (
                  <SelectItem key={h.value} value={h.value}>
                    {h.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="block text-xs text-gray-500 mb-1">Minute</Label>
            <Select value={minute} onValueChange={(val) => handleFieldChange('minute', val)}>
              <SelectTrigger className="w-full py-2.5" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MINUTES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    :{m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {preset === 'weekly' && (
        <div>
          <Label className="block text-xs text-gray-500 mb-1">Day of Week</Label>
          <Select value={dayOfWeek} onValueChange={(val) => handleFieldChange('dayOfWeek', val)}>
            <SelectTrigger className="w-full py-2.5" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAYS_OF_WEEK.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {preset === 'monthly' && (
        <div>
          <Label className="block text-xs text-gray-500 mb-1">Day of Month</Label>
          <Select value={dayOfMonth} onValueChange={(val) => handleFieldChange('dayOfMonth', val)}>
            <SelectTrigger className="w-full py-2.5" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAYS_OF_MONTH.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="pt-2 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          Expression: <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">{value || '—'}</code>
        </p>
      </div>
    </div>
  );
};
